// Pure HollowScript console formatting helpers, shared by editor-panels.tsx's
// `DockConsole` (Edit mode's docked Console tab / Preview's console overlay)
// and PlayScriptConsole.tsx (the public play surface's tier-2 console).
// Split out as its own dependency-free leaf module (no React, no editor
// state) rather than having PlayScriptConsole import these two functions
// from editor-panels.tsx directly - that file's own docked-panel UI surface
// is much larger, and this route should not pull it in for two formatting
// helpers. See narrowScriptCapabilities (editor-panels.tsx) for the one
// function these two consoles do share directly - that one is a real
// capability-narrowing policy worth keeping in one place with its own tests,
// not a leaf formatter.
import type { ScriptDiagnostic, ScriptValue } from '../../engine/scripting/scriptTypes';

/** Renders a HollowScript `ScriptValue` for the console log - a debug aid, not a
 * real formatter (no attempt at HollowScript's own literal syntax, just
 * something a creator can read at a glance). */
export function formatScriptValue(value: ScriptValue | undefined): string {
  if (value === undefined) return '(no value)';
  switch (value.kind) {
    case 'Int':
      return value.value.toString();
    case 'Float':
      return String(value.value);
    case 'Bool':
      return String(value.value);
    case 'String':
      return JSON.stringify(value.value);
    case 'None':
      return 'none';
    case 'List':
      return '[' + value.elements.map(formatScriptValue).join(', ') + ']';
    case 'Record':
      return '{' + [...value.fields.entries()].map(([key, field]) => `${key}: ${formatScriptValue(field)}`).join(', ') + '}';
    case 'Filter':
      return '<filter>';
    case 'Function':
      return '<function>';
  }
}

export function formatDiagnostic(diagnostic: ScriptDiagnostic): string {
  return diagnostic.message;
}
